import api from '@/lib/axios'

// ============================================================================
//  accounting.api.ts — Couche API Comptabilité
//  Consomme /api/v1/accounting (AccountingController — Phase 2)
//  [XOF] Devise de référence : Franc CFA Zone UEMOA
// ============================================================================

// ─── Types ───────────────────────────────────────────────────────────────────

export interface FiscalYear {
  id: string
  year: number
  label: string
  startDate: string
  endDate: string
  status: 'OPEN' | 'SOFT_CLOSED' | 'HARD_CLOSED' | 'REOPENED'
  softClosedAt?: string
  hardClosedAt?: string
  reopenedAt?: string
  createdAt: string
}

export interface Account {
  id: string
  code: string
  name: string
  shortName?: string
  class: number
  type: string
  level: number
  isAnalytic: boolean
  isActive: boolean
  isOhadaLocked: boolean
  isSystemAccount: boolean
  requiresApproval: boolean
  normalBalance: 'DEBIT' | 'CREDIT'
  parentId?: string
  parent?: { id: string; code: string; name: string }
}

export interface AccountBalance {
  accountCode: string
  accountName: string
  normalBalance: string
  totalDebit: number
  totalCredit: number
  balance: number
}

export interface JournalEntry {
  id: string
  reference: string
  date: string
  description: string
  totalDebit: number
  totalCredit: number
  currency: string
  status: 'DRAFT' | 'PENDING' | 'VALIDATED' | 'LOCKED' | 'REVERSED'
  fiscalYearId: string
  journalId: string
  journal?: { code: string; name: string; type: string }
  fiscalYear?: { year: number; label: string; status: string }
  lines: JournalEntryLine[]
  attachments?: EntryAttachment[]
  createdAt: string
  validatedAt?: string
}

export interface JournalEntryLine {
  id: string
  lineNumber: number
  debitAccountId?: string
  creditAccountId?: string
  debitAmount: number
  creditAmount: number
  description?: string
  taxRuleId?: string
  taxAmount?: number
  debitAccount?: { code: string; name: string }
  creditAccount?: { code: string; name: string }
  taxRule?: { code: string; name: string; rate: number }
}

export interface EntryAttachment {
  id: string
  fileName: string
  fileUrl: string
  fileSize: number
  mimeType: string
  isWormSealed: boolean
  wormSealedAt?: string
  hash?: string
}

export interface TaxRule {
  id: string
  code: string
  name: string
  type: string
  rate: number
  accountCode?: string
  isExempt: boolean
  territoriality?: string
  effectiveDate: string
  legalReference?: string
}

export interface TaxCalculationResult {
  amountHT: number
  taxAmount: number
  amountTTC: number
  taxRate: number
  taxRuleCode: string
  accountCode?: string
  isExempt: boolean
  legalReference?: string
}

export interface BalanceLine {
  accountCode: string
  accountName: string
  class: number
  totalDebit: number
  totalCredit: number
  balanceDebit: number
  balanceCredit: number
}

export interface TrialBalance {
  lines: BalanceLine[]
  totalDebit: number
  totalCredit: number
  isBalanced: boolean
  fiscalYear: { year: number; label: string; status: string }
}

export interface LedgerResult {
  account: { code: string; name: string; normalBalance: string }
  lines: LedgerLine[]
  openingBalance: number
  closingBalance: number
  totalDebit: number
  totalCredit: number
  total: number
  page: number
  pages: number
}

export interface LedgerLine {
  date: string
  journalCode: string
  reference: string
  description: string
  debitAmount: number
  creditAmount: number
  runningBalance: number
}

export interface CreateJournalEntryDto {
  journalId: string
  fiscalYearId: string
  date: string
  reference?: string
  description: string
  currency?: string
  lines: Array<{
    lineNumber: number
    debitAccountCode?: string
    creditAccountCode?: string
    debitAmount?: number
    creditAmount?: number
    description?: string
    taxRuleCode?: string
  }>
}

// ─── Types pagination alignés sur le format réel du backend ──────────────────
// Le backend retourne { entries, total, page, limit, pages } — pas PaginatedResponse<T>

export interface EntriesPage {
  entries: JournalEntry[]
  total: number
  page: number
  limit: number
  pages: number
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const accountingApi = {
  // ── Exercices fiscaux ──────────────────────────────────────────────────────

  getFiscalYears: () =>
    api.get<FiscalYear[]>('/accounting/fiscal-years').then((r) => r.data?.data ?? r.data),

  getFiscalYear: (id: string) =>
    api.get<FiscalYear>(`/accounting/fiscal-years/${id}`).then((r) => r.data?.data ?? r.data),

  createFiscalYear: (data: { year: number; label: string; startDate: string; endDate: string }) =>
    api.post<FiscalYear>('/accounting/fiscal-years', data).then((r) => r.data?.data ?? r.data),

  softCloseFiscalYear: (id: string, reason: string) =>
    api
      .post(`/accounting/fiscal-years/${id}/soft-close`, { reason })
      .then((r) => r.data?.data ?? r.data),

  hardCloseFiscalYear: (id: string, reason: string) =>
    api
      .post(`/accounting/fiscal-years/${id}/hard-close`, { reason })
      .then((r) => r.data?.data ?? r.data),

  reopenFiscalYear: (id: string, reason: string) =>
    api
      .post(`/accounting/fiscal-years/${id}/reopen`, { reason })
      .then((r) => r.data?.data ?? r.data),

  // ── Plan comptable ─────────────────────────────────────────────────────────

  getAccounts: (params?: {
    search?: string
    class?: number
    type?: string
    isActive?: boolean
    isAnalytic?: boolean
  }) => api.get<Account[]>('/accounting/accounts', { params }).then((r) => r.data?.data ?? r.data),

  getAccount: (id: string) =>
    api.get<Account>(`/accounting/accounts/${id}`).then((r) => r.data?.data ?? r.data),

  createAccount: (data: {
    code: string
    name: string
    type: string
    shortName?: string
    parentCode?: string
  }) => api.post<Account>('/accounting/accounts', data).then((r) => r.data?.data ?? r.data),

  updateAccount: (id: string, data: Partial<Account>) =>
    api.patch<Account>(`/accounting/accounts/${id}`, data).then((r) => r.data?.data ?? r.data),

  approveAccount: (id: string) =>
    api.post<Account>(`/accounting/accounts/${id}/approve`).then((r) => r.data?.data ?? r.data),

  getAccountBalance: (
    id: string,
    params?: { fiscalYearId?: string; startDate?: string; endDate?: string },
  ) =>
    api
      .get<AccountBalance>(`/accounting/accounts/${id}/balance`, { params })
      .then((r) => r.data?.data ?? r.data),

  // ── Écritures comptables ───────────────────────────────────────────────────

  getEntries: (params?: {
    fiscalYearId?: string
    journalId?: string
    status?: string
    search?: string
    page?: number
    limit?: number
  }) => api.get<EntriesPage>('/accounting/entries', { params }).then((r) => r.data?.data ?? r.data),

  getEntry: (id: string) =>
    api.get<JournalEntry>(`/accounting/entries/${id}`).then((r) => r.data?.data ?? r.data),

  createEntry: (dto: CreateJournalEntryDto) =>
    api.post<JournalEntry>('/accounting/entries', dto).then((r) => r.data?.data ?? r.data),

  updateEntry: (id: string, dto: Partial<CreateJournalEntryDto>) =>
    api.patch<JournalEntry>(`/accounting/entries/${id}`, dto).then((r) => r.data?.data ?? r.data),

  deleteEntry: (id: string) =>
    api.delete(`/accounting/entries/${id}`).then((r) => r.data?.data ?? r.data),

  validateEntry: (id: string) =>
    api
      .post<JournalEntry>(`/accounting/entries/${id}/validate`)
      .then((r) => r.data?.data ?? r.data),

  reverseEntry: (
    id: string,
    data: { reason: string; reversalType?: string; reversalDate?: string },
  ) =>
    api
      .post<JournalEntry>(`/accounting/entries/${id}/reverse`, data)
      .then((r) => r.data?.data ?? r.data),

  // ── Tax Engine ─────────────────────────────────────────────────────────────

  getTaxRules: () =>
    api.get<TaxRule[]>('/accounting/tax-rules').then((r) => r.data?.data ?? r.data),

  calculateTax: (data: { amountHT: number; taxRuleCode: string; clientNinea?: string }) =>
    api
      .post<TaxCalculationResult>('/accounting/tax-rules/calculate', data)
      .then((r) => r.data?.data ?? r.data),

  // ── Reporting ──────────────────────────────────────────────────────────────

  getTrialBalance: (
    fiscalYearId: string,
    params?: { startDate?: string; endDate?: string; classFilter?: string },
  ) =>
    api
      .get<TrialBalance>('/accounting/reports/trial-balance', {
        params: { fiscalYearId, ...params },
      })
      .then((r) => r.data?.data ?? r.data),

  getLedger: (
    accountCode: string,
    fiscalYearId: string,
    params?: { page?: number; limit?: number },
  ) =>
    api
      .get<LedgerResult>(`/accounting/reports/ledger/${accountCode}`, {
        params: { fiscalYearId, ...params },
      })
      .then((r) => r.data?.data ?? r.data),

  generateBalanceSheet: (fiscalYearId: string) =>
    api
      .post('/accounting/reports/balance-sheet', { fiscalYearId })
      .then((r) => r.data?.data ?? r.data),

  generateIncomeStatement: (fiscalYearId: string) =>
    api
      .post('/accounting/reports/income-statement', { fiscalYearId })
      .then((r) => r.data?.data ?? r.data),

  generateCashFlowTft: (fiscalYearId: string) =>
    api
      .post('/accounting/reports/cash-flow-tft', { fiscalYearId })
      .then((r) => r.data?.data ?? r.data),

  generateSyscohadaPackage: (fiscalYearId: string) =>
    api
      .post('/accounting/reports/syscohada-package', { fiscalYearId })
      .then((r) => r.data?.data ?? r.data),

  getTvaDeclaration: (startDate: string, endDate: string) =>
    api
      .get('/accounting/reports/tva', { params: { startDate, endDate } })
      .then((r) => r.data?.data ?? r.data),

  generateFec: (fiscalYearId: string) =>
    api.post('/accounting/reports/fec', { fiscalYearId }).then((r) => r.data?.data ?? r.data),
}
